import React, { useState, useEffect } from 'react';
import { isPhoneViewport } from '../../utils/mobileViewport';

/**
 * MobileTopBar — Matches the HTML reference design exactly.
 *
 * Structure:
 *   [List-icon btn] [SK-logo + page title] [SplitScreen btn] [Avatar btn]
 *
 * Heights (from reference):
 *   Top bar: 52px  (--topbar-h)
 *
 * Special variant: activePage === 'vibes' → dark brand-green background.
 *
 * Only rendered below 768px.
 */

const BRAND      = '#2d6b5e';
const BRAND_DARK = '#1e4a40';

interface MobileTopBarProps {
  /** Current active page key (e.g. 'dashboard', 'vibes', 'agenda') */
  activePage: string;
  /** Title shown in the center bar (e.g. 'SuperKonnected', 'Agenda', 'Vibes') */
  pageTitle: string;
  /** Two-letter user initials shown in the avatar circle */
  userInitials: string;
  /** Called when the List / hamburger button is tapped (cycles sub-tabs) */
  onListClick: () => void;
  /** Called when the avatar button is tapped (opens profile dropdown) */
  onAvatarClick: () => void;
  /** Fade top chrome while scrolling feed content */
  dimmed?: boolean;
}

const MobileTopBar: React.FC<MobileTopBarProps> = ({
  activePage,
  pageTitle,
  userInitials,
  onListClick,
  onAvatarClick,
  dimmed = false,
}) => {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const check = () => setIsMobile(isPhoneViewport());
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  if (!isMobile) return null;

  const isDark = activePage === 'vibes';

  const bg          = isDark ? BRAND_DARK : '#ffffff';
  const borderColor = isDark ? 'rgba(255,255,255,0.10)' : 'rgba(0,0,0,0.07)';
  const btnBg       = isDark ? 'rgba(255,255,255,0.10)' : '#ffffff';
  const btnBorder   = isDark ? 'rgba(255,255,255,0.20)' : 'rgba(0,0,0,0.12)';
  const iconColor   = isDark ? '#ffffff' : '#5a5852';
  const titleColor  = isDark ? '#ffffff' : '#1a1a18';

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: '50%',
        right: 'auto',
        transform: 'translateX(-50%)',
        width: '100%',
        maxWidth: '430px',
        height: '52px',
        backgroundColor: bg,
        borderBottom: `0.5px solid ${borderColor}`,
        display: 'flex',
        alignItems: 'center',
        padding: '0 12px',
        gap: '8px',
        zIndex: 9991,
        WebkitTapHighlightColor: 'transparent' as any,
        boxSizing: 'border-box',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          width: '100%',
          opacity: dimmed ? 0.15 : 1,
          transition: 'opacity 300ms ease-in-out',
        }}
      >
      {/* Left: List/cycle icon */}
      <button
        onClick={onListClick}
        aria-label="Volgende sectie"
        style={{
          width: 36, height: 36, flexShrink: 0,
          borderRadius: 6,
          border: `0.5px solid ${btnBorder}`,
          background: btnBg,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          cursor: 'pointer',
          padding: 0,
          outline: 'none',
          WebkitTapHighlightColor: 'transparent' as any,
          transition: 'transform 0.10s',
        }}
      >
        <svg width="17" height="13" viewBox="0 0 17 13" fill="none" aria-hidden="true">
          <rect x="0" y="0"   width="17" height="2" rx="1" fill={iconColor} />
          <rect x="0" y="5.5" width="11" height="2" rx="1" fill={iconColor} />
          <rect x="0" y="11"  width="14" height="2" rx="1" fill={iconColor} />
        </svg>
      </button>

      {/* Center: Logo + page title */}
      <div
        style={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 7,
          overflow: 'hidden',
        }}
      >
        {/* Real SuperKonnected logo — same asset as desktop/login */}
        {!isDark && (
          <img
            src="/SuperKonnected_Logo.svg"
            alt=""
            aria-hidden="true"
            style={{ width: 22, height: 22, objectFit: 'contain', flexShrink: 0 }}
            onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }}
          />
        )}
        <span
          style={{
            fontSize: 15,
            fontWeight: 600,
            color: titleColor,
            letterSpacing: '-0.01em',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            fontFamily: 'inherit',
          }}
        >
          {pageTitle}
        </span>
      </div>

      {/* Right: SplitScreen icon + Avatar */}
      <div style={{ display: 'flex', gap: 6, flexShrink: 0, alignItems: 'center' }}>
        {/* SplitScreen button */}
        <button
          aria-label="Splitscreen"
          style={{
            width: 36, height: 36,
            borderRadius: 6,
            border: `0.5px solid ${btnBorder}`,
            background: btnBg,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            cursor: 'pointer',
            padding: 0,
            outline: 'none',
            WebkitTapHighlightColor: 'transparent' as any,
          }}
        >
          <svg width="17" height="14" viewBox="0 0 17 14" fill="none" aria-hidden="true">
            <rect x="0" y="0"   width="17" height="5.5" rx="1.5" stroke={iconColor} strokeWidth="1.2" />
            <rect x="0" y="8.5" width="17" height="5.5" rx="1.5" stroke={iconColor} strokeWidth="1.2" />
          </svg>
        </button>

        {/* Avatar / initials button */}
        <button
          onClick={onAvatarClick}
          aria-label="Profiel menu openen"
          style={{
            width: 30, height: 30,
            borderRadius: '50%',
            background: BRAND_DARK,
            border: 'none',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 11, fontWeight: 600, color: '#fff',
            cursor: 'pointer',
            fontFamily: 'inherit',
            outline: 'none',
            WebkitTapHighlightColor: 'transparent' as any,
            flexShrink: 0,
            userSelect: 'none',
            transition: 'transform 0.10s',
          }}
        >
          {userInitials.slice(0, 2)}
        </button>
      </div>
      </div>
    </div>
  );
};

export default MobileTopBar;
